/**
 * Resolve the GitLab username of the person receiving access
 * (beneficiary), not necessarily the signed-in submitter.
 *
 * Priority:
 * 1. raising_for_other → other_username
 * 2. self_username (from signed-in identity / catalog User name)
 * 3. local-part of requester_email_id
 */

export type GitLabUsernameSection = {
  raising_for_other?: unknown;
  other_username?: unknown;
  self_username?: unknown;
};

export function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === 'true' || s === 'yes' || s === '1' || s === 'on';
  }
  return false;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function emailLocalPart(email: string): string {
  const at = email.indexOf('@');
  return (at > 0 ? email.slice(0, at) : email).trim();
}

export function deriveAccessRequestBeneficiaryUsername(
  formData: Record<string, unknown> | undefined,
): string {
  if (!formData) return '';

  const section = formData.gitlab_username_section as
    | GitLabUsernameSection
    | undefined;

  const raisingForOther =
    isTruthyFlag(section?.raising_for_other) ||
    isTruthyFlag(formData['gitlab_username_section.raising_for_other']);

  if (raisingForOther) {
    return (
      asTrimmedString(section?.other_username) ||
      asTrimmedString(formData['gitlab_username_section.other_username'])
    );
  }

  const selfUsername =
    asTrimmedString(section?.self_username) ||
    asTrimmedString(formData['gitlab_username_section.self_username']);
  if (selfUsername) {
    return selfUsername;
  }

  const email = asTrimmedString(formData.requester_email_id);
  return email ? emailLocalPart(email) : '';
}

/**
 * Email for Pub/Sub `email` field: submitter when self; empty when raising for
 * another user (submitter goes in `requested_by` from the template).
 */
export function deriveAccessRequestBeneficiaryEmail(
  formData: Record<string, unknown> | undefined,
): string {
  if (!formData) return '';
  const section = formData.gitlab_username_section as
    | GitLabUsernameSection
    | undefined;
  const raisingForOther =
    isTruthyFlag(section?.raising_for_other) ||
    isTruthyFlag(formData['gitlab_username_section.raising_for_other']);
  if (raisingForOther) {
    return '';
  }
  return asTrimmedString(formData.requester_email_id);
}
